import React from 'react';
import { defineMessages, useIntl, type MessageValue, type NoMessageValues } from 'react-intl';
import { Platform, StyleSheet } from 'react-native';

import type { AddressType } from '@sg/garnish';
import { Button, DisplayText, HStack, IconLink, Modal, theme, useResponsive } from '@sg/garnish';

import { DefaultHeaderLeft } from '../../../../routing/components/DefaultHeaderLeft';
import { useNavigationActions } from '../../hooks';

export const ReorderTitle = ({
  orderType,
}: Readonly<{
  orderType: AddressType;
}>) => {
  const { formatMessage } = useIntl();
  const { currentBreakpoint } = useResponsive();

  return (
    <DisplayText
      size={4}
      style={[styles.headline, currentBreakpoint.isXS && styles.mobileHeadline]}
    >
      {formatMessage(messages.reorderConfirmLocation, { channel: orderType })}
    </DisplayText>
  );
};

export const ReorderModalFooter = ({ isLoading, onContinue, onMoreLocations }: FooterProps) => {
  const { formatMessage } = useIntl();
  const { currentBreakpoint } = useResponsive();

  return (
    <Modal.Footer style={styles.footer} withoutTopBorder>
      <HStack
        style={currentBreakpoint.isXS ? styles.mobileStack : undefined}
        itemsPerRow={2}
        gap={theme.spacing['4']}
      >
        <Button
          size="large"
          palette="secondary"
          onPress={onMoreLocations}
          testID="reorder.more-locations"
          accessibilityLabel={formatMessage(messages.reorderMoreLocations)}
          accessibilityHint={formatMessage(messages.reorderMoreLocations)}
          accessibilityRole="button"
        >
          {formatMessage(messages.reorderMoreLocations)}
        </Button>
        <Button
          size="large"
          onPress={onContinue}
          isLoading={isLoading}
          testID="reorder.continue-to-bag"
          accessibilityLabel={formatMessage(messages.reorderContinueToBag)}
          accessibilityHint={formatMessage(messages.reorderContinueToBag)}
          accessibilityRole="button"
        >
          {formatMessage(messages.reorderContinueToBag)}
        </Button>
      </HStack>
    </Modal.Footer>
  );
};

export const useReorderLeftIcon = () => {
  const { formatMessage } = useIntl();
  const { onBack } = useNavigationActions();

  return (
    <DefaultHeaderLeft
      onPress={onBack}
      accessibilityLabel={formatMessage(messages.reorderConfirmLocationBack)}
    />
  );
};

export const useReorderRightIcon = () => {
  const { formatMessage } = useIntl();
  const { onClose } = useNavigationActions();

  return (
    <IconLink
      width={36}
      height={36}
      testID="location.search-bar.close-button"
      name="IconClose"
      palette="muted"
      style={styles.closeIcon}
      accessibilityLabel={formatMessage(messages.reorderConfirmLocationClose)}
      onPress={onClose}
    />
  );
};

export type FooterProps = Readonly<{
  isLoading?: boolean;
  onContinue: () => void;
  onMoreLocations: () => void;
}>;

const styles = StyleSheet.create({
  mobileHeadline: {
    fontSize: 18,
    paddingHorizontal: theme.spacing['4'],
    paddingBottom: 0,
    paddingTop: 0,
  },
  headline: {
    paddingHorizontal: theme.spacing['6'],
    paddingTop: theme.spacing['6'],
    paddingBottom: theme.spacing['2'],
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    paddingHorizontal: theme.spacing['4'],
    marginTop: 'auto',
  },
  mobileStack: {
    borderTopWidth: 1,
    borderTopColor: theme.colors.LIGHT,
    paddingTop: theme.spacing['4'],
  },
  closeIcon: {
    marginRight: Platform.select({
      web: theme.spacing['4'],
      default: 0,
    }),
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly reorderConfirmLocationClose: NoMessageValues;
  readonly reorderConfirmLocation: { readonly channel: MessageValue };
  readonly reorderMoreLocations: NoMessageValues;
  readonly reorderContinueToBag: NoMessageValues;
  readonly reorderConfirmLocationBack: NoMessageValues;
}>({
  reorderConfirmLocationClose: {
    defaultMessage: 'Close',
    description: 'Reorder Confirm Location | Close',
  },
  reorderConfirmLocation: {
    defaultMessage: 'Confirm {channel} location',
    description: 'Reorder | Confirm Location',
  },
  reorderMoreLocations: {
    defaultMessage: 'More locations',
    description: 'Reorder | More Locations',
  },
  reorderContinueToBag: {
    defaultMessage: 'Continue to bag',
    description: 'Reorder | Continue To Bag',
  },
  reorderConfirmLocationBack: {
    defaultMessage: 'Back',
    description: 'Reorder Confirm Location | Back',
  },
});
